import type { IntlShape } from 'react-intl';
import type { AppDeliverableSpec, AppManifest, AppRunsOn } from '@api/apps';
import { classifyAttachment } from '@components/Attachments/attachmentKind';
import { displayFormats } from './appFormats';

// An app that admits screenshots among its deliverables gets its pictures as
// renders of the real file, so the card names only the file the app is for.
const filesDelivered = ({ extensions, artifactTypes }: AppDeliverableSpec): readonly string[] => {
  if (!artifactTypes.includes('SCREENSHOT')) return extensions;
  const files = extensions.filter((ext) => classifyAttachment('', `render.${ext}`) !== 'image');
  return files.length > 0 ? files : extensions;
};

export interface CardFormats {
  readonly takes: readonly string[];
  readonly delivers: readonly string[];
}

export const cardFormatsOf = (manifest: AppManifest): CardFormats => ({
  takes: manifest.inputs.acceptedExtensions,
  delivers: manifest.deliverable ? filesDelivered(manifest.deliverable) : [],
});

export const runsOnLabel = (intl: IntlShape, runsOn: AppRunsOn | undefined): string | null => {
  if (runsOn === 'LOCAL') {
    return intl.formatMessage({ id: 'apps.gallery.card.runsOn.local', defaultMessage: 'Runs on your computer' });
  }
  if (runsOn === 'CHOICE') {
    return intl.formatMessage({ id: 'apps.gallery.card.runsOn.choice', defaultMessage: 'Cloud or your computer' });
  }
  return null;
};

interface CardLabelParts {
  readonly name: string;
  readonly tagline: string;
  readonly formats: CardFormats;
  readonly runsOn: string | null;
}

const summaryOf = (intl: IntlShape, name: string, tagline: string, { takes, delivers }: CardFormats): string => {
  const values = {
    name,
    tagline,
    takes: displayFormats(takes).join(', '),
    delivers: displayFormats(delivers).join(', '),
  };
  if (takes.length > 0 && delivers.length > 0) {
    return intl.formatMessage(
      { id: 'apps.gallery.card.label', defaultMessage: '{name}. {tagline}. Takes {takes}, delivers {delivers}.' },
      values,
    );
  }
  if (takes.length > 0) {
    return intl.formatMessage(
      { id: 'apps.gallery.card.label.takes', defaultMessage: '{name}. {tagline}. Takes {takes}.' },
      values,
    );
  }
  if (delivers.length > 0) {
    return intl.formatMessage(
      { id: 'apps.gallery.card.label.delivers', defaultMessage: '{name}. {tagline}. Delivers {delivers}.' },
      values,
    );
  }
  return intl.formatMessage({ id: 'apps.gallery.card.label.plain', defaultMessage: '{name}. {tagline}.' }, values);
};

export const cardLabel = (intl: IntlShape, { name, tagline, formats, runsOn }: CardLabelParts): string =>
  [summaryOf(intl, name, tagline, formats), runsOn].filter(Boolean).join(' ');
